import React, { useMemo, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { observer } from 'mobx-react';
import axios, { AxiosError } from 'axios';
import { usePagination, useStore } from '../../../common/hooks';
import { Label, Creatable, Input } from '../../../common/materials';
import { UserCompany } from '../../../models/user/UserCompany';
import { Pagination } from '../../../common/components';
import { F1ResetPasswordResponse } from "../../../models/authentication/F1ResetPasswordResponse";
import { UserCompanyTable, UserCredentialFormModal, ChangePasswordResponseModal } from './components';

export const UserAuthentication = observer(() => {
    const { userCompanyStore, companyMasterStore } = useStore();
    const { userRole, citizenId, currentAdminCompany } = userCompanyStore;
    const [isSCG, setIsSCG] = useState(false);
    const [searchPattern, setSearchPattern] = useState('');
    const [f1ResetPasswordResponse, setF1ResetPasswordResponse] = useState<F1ResetPasswordResponse[]>([]);

    useEffect(() => {
        fetchData();
    }, []);
    const fetchData = async () => {
        await Promise.allSettled([fetchIsSCG()]);
    };


    const fetchIsSCG = async () => {
        const data = (await axios.get('/v1/link-sid')).data;
        setIsSCG(data.link_sid === 'SCG');
    };

    const { data: userCompanies = [], isFetching: isFetchingUserCompanies } = useQuery({
        enabled: !!citizenId,
        queryKey: [citizenId, 'user-companies', { sid: '' }],
        queryFn: ({ signal }) => userCompanyStore.fetchAllUserCompany('', '', '', { signal }),
        refetchOnWindowFocus: false,
        retry: 1
    });
    const uniqueUserCompanies = useMemo(() => {
        const seenCitizenId = new Set();
        return userCompanies
            .filter(user => {
                if (!seenCitizenId.has(user.citizen_id)) {
                    seenCitizenId.add(user.citizen_id);
                    return true;
                }

                return false;
            })
            .filter(user => {
                const lowerCaseSearchPattern = searchPattern.toLowerCase();
                return (
                    user.citizen_id?.toLowerCase().includes(lowerCaseSearchPattern) ||
                    user.email?.toLowerCase().includes(lowerCaseSearchPattern)
                );
            });
    }, [searchPattern, userCompanies]);

    const {
        collections: slicedFilterUsers,
        numberOfPages,
        currentPage,
        handleClickFirstPage,
        handleClickPreviousPage,
        handleClickNextPage,
        handleClickLastPage
    } = usePagination(uniqueUserCompanies, {
        initialPage: 1,
        pageSize: 50
    });

    const [selectedUser, setSelectedUser] = useState<UserCompany>();
    const isOpenUserCredentialFormModal = !!selectedUser;

    const handleCloseUserCredentialFormModal = () => {
        setSelectedUser(undefined);
    };

    const handleCloseChangePasswordResponseModal = () => {
        setF1ResetPasswordResponse([]);
    }

    const paginationJSX = (
        <Pagination
            currentPage={currentPage}
            numberOfPages={numberOfPages}
            handleClickFirstPage={handleClickFirstPage}
            handleClickNextPage={handleClickNextPage}
            handleClickPreviousPage={handleClickPreviousPage}
            handleClickLastPage={handleClickLastPage}
            className="ml-auto"
        />
    );

    return (
        <React.Fragment>
            <div className="flex flex-col gap-4">
                <p className="text-2xl font-semibold">User Authentication</p>
            </div>
            <div className="bg-white p-4">
                <div className="mt-4 flex flex-col gap-1">
                    <Label>Username</Label>
                    <div className="flex gap-2 bg-white">
                        <Input
                            className="w-1/2"
                            type="text"
                            value={searchPattern}
                            autoFocus={true}
                            placeholder="search users"
                            onChange={event => setSearchPattern(event.target.value)}
                        />
                    </div>
                </div>
            </div>

            <div className="ml-4 mt-4 flex flex-col md:flex-row md:items-baseline lg:flex-col xl:flex-row">
                {paginationJSX}
            </div>

            <UserCompanyTable
                isFetching={isFetchingUserCompanies}
                userCompanies={userRole === 'User' ? [] : slicedFilterUsers}
                onEditCredential={setSelectedUser}
            />

            <UserCredentialFormModal
                isSCG={isSCG}
                user={selectedUser}
                isOpen={isOpenUserCredentialFormModal}
                onClose={handleCloseUserCredentialFormModal}
                onF1ResetPasswordResponseChange={setF1ResetPasswordResponse}
            />
            <ChangePasswordResponseModal
                isOpen={(f1ResetPasswordResponse.length > 0)}
                f1ResetPasswordResponse={f1ResetPasswordResponse}
                onClose={handleCloseChangePasswordResponseModal}
            />
        </React.Fragment>
    );
});
